import type { Theme } from "@fobu/schema";

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "").slice(0, 6);
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** Relative Leuchtdichte nach WCAG 2.2. */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Kontrastverhältnis (1–21). AA: ≥ 4,5 für Text, ≥ 3 für Bedienelemente. */
export function contrast(a: string, b: string): number {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (l1 + 0.05) / (l2 + 0.05);
}

/** Lesbare Schriftfarbe auf einer Fläche. */
export const onColor = (bg: string) => (contrast(bg, "#FFFFFF") >= contrast(bg, "#000000") ? "#FFFFFF" : "#000000");

export const rgba = (hex: string, alpha: number) => `rgba(${hexToRgb(hex).join(", ")}, ${alpha})`;

export interface ContrastWarning {
  pair: string;
  ratio: number;
  required: number;
}

/** Farbkombinationen eines Themes, die WCAG AA verfehlen. */
export function themeContrastWarnings(theme: Theme): ContrastWarning[] {
  const c = theme.colors;
  const checks: Array<[string, string, string, number]> = [
    ["Text auf Hintergrund", c.text, c.background, 4.5],
    ["Text auf Fläche", c.text, c.surface, 4.5],
    ["Nebentext auf Fläche", c.muted, c.surface, 4.5],
    ["Nebentext auf Hintergrund", c.muted, c.background, 4.5],
    ["Akzentfarbe auf Fläche (Bedienelemente)", c.primary, c.surface, 3],
    ["Fehlerfarbe auf Fläche", c.danger, c.surface, 4.5],
  ];
  return checks
    .map(([pair, fg, bg, required]) => ({ pair, ratio: Math.round(contrast(fg, bg) * 100) / 100, required }))
    .filter((w) => w.ratio < w.required);
}
